<template>
  <FormComponent
    title="Add Vendor Tariff"
    :backRoute="backRoute"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #formActions>
      <v-btn color="primary" depressed @click="submitForm" :disabled="!isValid">
        Submit
      </v-btn>
      <v-btn color="secondary" class="ml-2" depressed @click="resetForm">
        Reset
      </v-btn>
    </template>

    <template #formContent>
      <v-form ref="contractForm" v-model="isValid">
        <v-row class="mx-2 my-0">
          <v-col cols="12" class="Form-Columns-Dense">
            <span class="text-h6 font-weight-regular">Tariff Details</span>
          </v-col>

          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <Autocomplete
              :loading="transporterLoading"
              :totalItems="transporterCount"
              :items="allTransporterList"
              @updateList="getTransporterList"
              label="Select Transporter *"
              itemText="company_name"
              itemValue="id"
              :rules="[(v) => !!v || 'Transporter is Required']"
              hide-details="auto"
              v-model="contractDetails.transporter"
              :error-messages="formErrors.transporter"
              @input="delete formErrors.transporter"
            />
          </v-col>

          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <MultiSelect
              label="Job Type *"
              :items="allJobType"
              itemText="display_name"
              itemValue="value"
              :rules="[(v) => !!v || 'Job Type is Required']"
              hide-details="auto"
              v-model="contractDetails.job_type"
              :error-messages="formErrors.job_type"
              @input="delete formErrors.job_type"
            />
          </v-col>

          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <DateField
              :inputProps="{
                dense: true,
                label: 'Start Date *',
                'hide-details': 'auto',
                rules: [(val) => !!val || 'Start Date is required!'],
                'error-messages': formErrors.start_date,
              }"
              required
              v-model="contractDetails.start_date"
              @input="delete formErrors.start_date"
            />
          </v-col>

          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <DateField
              :inputProps="{
                dense: true,
                label: 'End Date *',
                'hide-details': 'auto',
                rules: [(val) => !!val || 'End Date is required!'],
                'error-messages': formErrors.end_date,
              }"
              required
              :min="contractDetails.start_date"
              v-model="contractDetails.end_date"
              @input="delete formErrors.end_date"
            />
          </v-col>

          <v-col cols="12" lg="6" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Admin Remarks"
              hide-details="auto"
              v-model="contractDetails.admin_remarks"
              :error-messages="formErrors.admin_remarks"
              @input="delete formErrors.admin_remarks"
            />
          </v-col>

          <v-col cols="12" lg="6" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Transporter Remarks"
              hide-details="auto"
              v-model="contractDetails.transporter_remarks"
              :error-messages="formErrors.transporter_remarks"
              @input="delete formErrors.transporter_remarks"
            />
          </v-col>

          <!-- Rates -->
          <v-col cols="12" class="Form-Columns-Dense mt-2">
            <span class="text-h6 font-weight-regular">Rate Details</span>
          </v-col>

          <v-col cols="12">
            <v-row
              v-for="(rate, i) in contractDetails.transporter_contract_rates"
              :key="i"
              class="my-1"
            >
              <v-col cols="12" lg="3" class="Form-Columns-Dense">
                <Autocomplete
                  :loading="laneLoading"
                  :totalItems="laneCount"
                  :items="allLaneList"
                  @updateList="getLaneList"
                  label="Route"
                  itemText="lane_title"
                  itemValue="id"
                  hide-details="auto"
                  v-model="rate.lane"
                  :error-messages="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i] &&
                    formErrors.transporter_contract_rates[i].lane
                  "
                  @input="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i]
                      ? delete formErrors.transporter_contract_rates[i].lane
                      : null
                  "
                />
              </v-col>

              <v-col cols="12" lg="3" class="Form-Columns-Dense">
                <MultiSelect
                  label="Container Type *"
                  :items="allContainerTypes"
                  itemText="type_name"
                  itemValue="id"
                  :rules="[(v) => !!v || 'Container Type is Required']"
                  hide-details="auto"
                  v-model="rate.container_type"
                  :error-messages="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i] &&
                    formErrors.transporter_contract_rates[i].container_type
                  "
                  @input="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i]
                      ? delete formErrors.transporter_contract_rates[i].container_type
                      : null
                  "
                />
              </v-col>

              <v-col cols="12" lg="2" class="Form-Columns-Dense">
                <InputField
                  type="number"
                  label="Count *"
                  :rules="[(v) => !!v || 'Required']"
                  hide-details="auto"
                  v-model="rate.container_count"
                  :error-messages="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i] &&
                    formErrors.transporter_contract_rates[i].container_count
                  "
                />
              </v-col>

              <v-col cols="12" lg="2" class="Form-Columns-Dense">
                <InputField
                  type="number"
                  label="Rate *"
                  :rules="[(v) => !!v || 'Required']"
                  hide-details="auto"
                  v-model="rate.rate"
                  :error-messages="
                    formErrors.transporter_contract_rates &&
                    formErrors.transporter_contract_rates[i] &&
                    formErrors.transporter_contract_rates[i].rate
                  "
                />
              </v-col>

              <v-col cols="12" lg="2" class="Form-Columns-Dense d-flex align-center">
                <v-btn icon color="error" @click="removeRate(i)" :disabled="contractDetails.transporter_contract_rates.length === 1">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-btn depressed color="primary" small class="mt-2" @click="addRate">
              <v-icon left small>mdi-plus</v-icon> Add Rate
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </FormComponent>
</template>

<script>
import FormComponent from "@/components/General/FormComponent.vue";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import { isAuthenticated, hasRouteAccess, getPermissions } from "@/utils/functions";

export default {
  components: { FormComponent, Autocomplete, MultiSelect, InputField, DateField },
  data() {
    return {
      isValid: false,
      formErrors: {},
      backRoute: { path: "/app/admin/transporter-contract/list" },

      contractDetails: {
        transporter: null,
        job_type: null,
        start_date: null,
        end_date: null,
        admin_remarks: null,
        transporter_remarks: null,
        transporter_contract_rates: [{ lane: null, container_type: null, container_count: 1, rate: null }],
      },

      allTransporterList: [],
      transporterCount: 0,
      transporterLoading: false,

      allLaneList: [],
      laneCount: 0,
      laneLoading: false,

      allContainerTypes: [],
      allJobType: [],
    };
  },
  methods: {
    addRate() {
      this.contractDetails.transporter_contract_rates.push({
        lane: null, container_type: null, container_count: 1, rate: null,
      });
    },
    removeRate(index) {
      this.contractDetails.transporter_contract_rates.splice(index, 1);
    },
    resetForm() {
      this.$refs.contractForm.reset();
      this.contractDetails.transporter_contract_rates = [
        { lane: null, container_type: null, container_count: 1, rate: null },
      ];
      this.formErrors = {};
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      this.$api.transporterContract
        .addTransporterContract(this.contractDetails)
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", { message: "Tariff added successfully!", color: "success" });
          this.$router.push(this.backRoute);
        })
        .catch((err) => {
          this.formErrors = err.data || {};
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", { message: "Failed to add tariff!", color: "error" });
        });
    },
    getTransporterList(params = {}) {
      this.transporterLoading = true;
      this.$api.company
        .getCompanyList({ company_type: "Transporter", limit: 20, ...params })
        .then((res) => {
          this.allTransporterList = [...this.allTransporterList, ...res.data];
          this.transporterCount = res.count;
          this.transporterLoading = false;
        })
        .catch(() => { this.transporterLoading = false; });
    },
    getLaneList(params = {}) {
      this.laneLoading = true;
      this.$api.lane
        .getLaneList({ limit: 20, ...params })
        .then((res) => {
          this.allLaneList = [...this.allLaneList, ...res.data];
          this.laneCount = res.count;
          this.laneLoading = false;
        })
        .catch(() => { this.laneLoading = false; });
    },
    getContainerTypes() {
      this.$api.containerType
        .getContainerTypeList({ limit: 100 })
        .then((res) => { this.allContainerTypes = res.data; })
        .catch(() => {});
    },
    getJobTypes() {
      this.$api.transporterContract
        .getTransporterContractOptions()
        .then((res) => {
          const jobTypeField = res.data && res.data.actions && res.data.actions.POST && res.data.actions.POST.job_type;
          this.allJobType = jobTypeField ? jobTypeField.choices : [];
        })
        .catch(() => {
          this.allJobType = [
            { display_name: "Import", value: "Import" },
            { display_name: "Export", value: "Export" },
            { display_name: "FCL", value: "FCL" },
            { display_name: "LCL", value: "LCL" },
            { display_name: "Empty", value: "Empty" },
          ];
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("contract");
      if (!permissions || !permissions.add) {
        this.$bus.$emit("showToastMessage", { message: "You don't have access to this page", color: "error" });
        this.$router.push(`/${localStorage.getItem("user_type") ? localStorage.getItem("user_type").toLowerCase() : "error"}`);
      } else {
        this.getTransporterList();
        this.getLaneList();
        this.getContainerTypes();
        this.getJobTypes();
      }
    }
  },
};
</script>
